import { useCallback, useId, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Button, Input, Modal, Select, Textarea, Toast } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { DATA_PRODUCTS_WRITE_PERMISSION } from "@/constants/permissions";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import {
  dataProducts,
  type AdminDashboard,
  type DashboardCategory,
  type DashboardKind,
  type DashboardPreview as DashboardPreviewData,
  type DashboardStatus,
  type RequiredProduct,
} from "@/api/dataProducts.service";
import { DashboardConfigurationWarning } from "./DashboardConfigurationWarning";
import { DashboardPreview } from "./DashboardPreview";
import {
  describePreviewError,
  describeSaveError,
  emptyFormValues,
  toDashboardInput,
  toFormValues,
  validateDashboardForm,
  type DashboardFormErrors,
  type DashboardFormValues,
  type PreviewFailure,
} from "./dashboardForm";
import {
  CATEGORY_OPTIONS,
  DASHBOARD_KIND_CONFIG,
  EDITION_SUGGESTIONS,
  REGION_OPTIONS,
  REQUIRED_PRODUCT_OPTIONS,
  STATUS_OPTIONS,
} from "./dashboardKinds";
import styles from "./DataProducts.module.css";

export type DashboardEditProps = {
  kind: DashboardKind;
};

type DashboardFormProps = {
  routeKind: DashboardKind;
  dashboard: AdminDashboard | null;
  canWrite: boolean;
  onSaved: (dashboard: AdminDashboard) => void;
};

const REGION_SELECT_OPTIONS = [{ value: "", label: "Select a region…" }, ...REGION_OPTIONS];

function DashboardForm({ routeKind, dashboard, canWrite, onSaved }: DashboardFormProps) {
  const kind = dashboard?.kind ?? routeKind;
  const config = DASHBOARD_KIND_CONFIG[kind];
  const navigate = useNavigate();
  const editionListId = useId();

  const [values, setValues] = useState<DashboardFormValues>(() =>
    dashboard ? toFormValues(dashboard) : emptyFormValues(kind),
  );
  const [errors, setErrors] = useState<DashboardFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<AdminDashboard | null>(dashboard);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const [preview, setPreview] = useState<DashboardPreviewData | null>(null);
  const [previewError, setPreviewError] = useState<PreviewFailure | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const readOnly = !canWrite || saving;
  const isOmni = kind === "omni";
  const isTableau = kind === "tableau";

  const setField = <K extends keyof DashboardFormValues>(key: K, value: DashboardFormValues[K]) => {
    setValues(current => ({ ...current, [key]: value }));
    setErrors(current => ({ ...current, [key]: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canWrite) return;

    const nextErrors = validateDashboardForm(kind, values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    setSaveError(null);
    const input = toDashboardInput(kind, values);
    try {
      if (saved) {
        const updated = await dataProducts.update(saved.id, input);
        setSaved(updated);
        setValues(toFormValues(updated));
        onSaved(updated);
        setToast({ message: `"${updated.title}" saved.`, type: "success" });
      } else {
        const created = await dataProducts.create(input);
        navigate(config.detailRoute(created.id), { replace: true });
      }
    } catch (error) {
      const described = describeSaveError(error);
      setErrors(current => ({ ...current, ...described.fields }));
      setSaveError(described.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!saved) return;
    setRemoving(true);
    setDeleteError(null);
    try {
      await dataProducts.remove(saved.id);
      navigate(config.listRoute, { replace: true });
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "The dashboard could not be deleted");
    } finally {
      setRemoving(false);
    }
  };

  const openPreview = useCallback(async () => {
    if (!saved) return;
    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);
    try {
      // Never kept: an Omni URL is signed per request and expires.
      const next = await dataProducts.preview(saved.id);
      if (next.kind === "tableau") {
        window.open(next.url, "_blank", "noopener,noreferrer");
      }
      setPreview(next);
    } catch (error) {
      setPreviewError(describePreviewError(error, saved.configurationIssues));
    } finally {
      setPreviewing(false);
    }
  }, [saved]);

  return (
    <>
      {saved && <DashboardConfigurationWarning issues={saved.configurationIssues} />}
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Dashboard</legend>

          <Input
            label="Title"
            value={values.title}
            error={errors.title}
            fullWidth
            disabled={readOnly}
            onChange={e => setField("title", e.target.value)}
          />

          <div className={styles.formRow}>
            <Input
              label="Slug"
              value={values.slug}
              error={errors.slug}
              fullWidth
              disabled={readOnly}
              helperText="Lowercase letters, digits and hyphens. An internal identifier, unique across dashboards; customers open a dashboard by its id."
              onChange={e => setField("slug", e.target.value)}
            />
            <Select
              label="Status"
              value={values.status}
              error={errors.status}
              fullWidth
              disabled={readOnly}
              options={STATUS_OPTIONS}
              onChange={e => setField("status", e.target.value as DashboardStatus)}
            />
          </div>

          <Textarea
            label="Description"
            value={values.description}
            error={errors.description}
            rows={2}
            fullWidth
            disabled={readOnly}
            helperText="Optional. Shown under the title in the customer's catalogue."
            onChange={e => setField("description", e.target.value)}
          />

          {isOmni && (
            <div className={styles.formRow}>
              <Select
                label="Category"
                value={values.category}
                error={errors.category}
                fullWidth
                disabled={readOnly}
                options={CATEGORY_OPTIONS}
                onChange={e => setField("category", e.target.value as DashboardCategory)}
              />
              <Select
                label="Required product"
                value={values.requiredProduct}
                error={errors.requiredProduct}
                fullWidth
                disabled={readOnly}
                helperText="The product a firm must hold to open this dashboard."
                options={REQUIRED_PRODUCT_OPTIONS}
                onChange={e => setField("requiredProduct", e.target.value as RequiredProduct)}
              />
            </div>
          )}

          {isTableau && (
            <div className={styles.formRow}>
              <Select
                label="Region"
                value={values.regionId}
                error={errors.regionId}
                fullWidth
                disabled={readOnly}
                helperText="The Historical Data region a firm must be entitled to."
                options={REGION_SELECT_OPTIONS}
                onChange={e => setField("regionId", e.target.value)}
              />
              <Input
                label="Edition"
                value={values.edition}
                error={errors.edition}
                fullWidth
                disabled={readOnly}
                list={editionListId}
                helperText="A year such as 2026, or all_years."
                onChange={e => setField("edition", e.target.value)}
              />
              <datalist id={editionListId}>
                {EDITION_SUGGESTIONS.map(edition => (
                  <option key={edition} value={edition} />
                ))}
              </datalist>
            </div>
          )}

          <Input
            label="Sort order"
            type="number"
            step={1}
            value={values.sortOrder}
            error={errors.sortOrder}
            disabled={readOnly}
            helperText="Lower numbers come first in the customer's list."
            onChange={e => setField("sortOrder", e.target.value)}
          />
        </fieldset>

        {isOmni && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Omni embed</legend>
            <Input
              label="Omni content id"
              value={values.omniContentId}
              error={errors.omniContentId}
              fullWidth
              disabled={readOnly}
              helperText="Leave blank to use the environment default."
              onChange={e => setField("omniContentId", e.target.value)}
            />
            <Input
              label="Link access ids"
              value={values.omniLinkAccess}
              error={errors.omniLinkAccess}
              fullWidth
              disabled={readOnly}
              helperText="Comma-separated. Other Omni content the embed may link out to."
              onChange={e => setField("omniLinkAccess", e.target.value)}
            />
            <Input
              label="Custom theme id"
              value={values.omniCustomThemeId}
              error={errors.omniCustomThemeId}
              fullWidth
              disabled={readOnly}
              helperText="Optional."
              onChange={e => setField("omniCustomThemeId", e.target.value)}
            />
          </fieldset>
        )}

        {isTableau && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Tableau view</legend>
            <Input
              label="Tableau host"
              type="url"
              value={values.tableauHost}
              error={errors.tableauHost}
              fullWidth
              disabled={readOnly}
              helperText="An https URL, normally https://public.tableau.com."
              onChange={e => setField("tableauHost", e.target.value)}
            />
            <Input
              label="Tableau view path"
              value={values.tableauViewPath}
              error={errors.tableauViewPath}
              fullWidth
              disabled={readOnly}
              helperText="The workbook and view, e.g. 2026_UK_Complete/Rankingsataglance."
              onChange={e => setField("tableauViewPath", e.target.value)}
            />
          </fieldset>
        )}

        {saveError && (
          <p className={styles.error} role="alert">
            {saveError}
          </p>
        )}

        {previewError && (
          <div className={styles.error} role="alert">
            <p>{previewError.message}</p>
            {previewError.issues.length > 0 && (
              <ul className={styles.errorList}>
                {previewError.issues.map(issue => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!canWrite && (
          <p className={styles.hint}>
            You can view this dashboard but not change it — that needs the data products write
            permission.
          </p>
        )}

        <div className={styles.formActions}>
          {saved && (
            <Button
              type="button"
              variant="outlined"
              color="default"
              className={styles.spacer}
              onClick={openPreview}
              disabled={previewing}
            >
              {previewing ? "Generating preview…" : "Preview"}
            </Button>
          )}
          {saved && canWrite && (
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={() => {
                setDeleteError(null);
                setConfirmingDelete(true);
              }}
              disabled={saving}
            >
              Delete
            </Button>
          )}
          {canWrite && (
            <Button type="submit" variant="contained" color="primary" disabled={saving}>
              {saving ? "Saving…" : saved ? "Save" : "Create dashboard"}
            </Button>
          )}
        </div>
      </form>

      {preview && (
        <DashboardPreview
          preview={preview}
          title={saved?.title ?? values.title}
          onClose={() => setPreview(null)}
        />
      )}

      <Modal
        isOpen={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title="Delete dashboard"
        size="small"
      >
        <p>
          Delete "{saved?.title}"? Customers lose it from their catalogue immediately. This cannot
          be undone.
        </p>
        {deleteError && (
          <p className={styles.error} role="alert">
            {deleteError}
          </p>
        )}
        <div className={styles.formActions}>
          <Button
            type="button"
            variant="outlined"
            color="default"
            onClick={() => setConfirmingDelete(false)}
            disabled={removing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={removing}
          >
            {removing ? "Deleting…" : "Delete dashboard"}
          </Button>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

export function DashboardEdit({ kind }: DashboardEditProps) {
  const config = DASHBOARD_KIND_CONFIG[kind];
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission(DATA_PRODUCTS_WRITE_PERMISSION);
  const isNew = id === undefined;

  const { data, loading, error, refetch } = useQuery(
    () => (isNew ? Promise.resolve(null) : dataProducts.get(id)),
    [id],
  );
  const [savedTitle, setSavedTitle] = useState<string | null>(null);

  if (isNew && !canWrite) {
    return <UnauthorizedView />;
  }

  if (data && data.kind !== kind) {
    return <Navigate to={DASHBOARD_KIND_CONFIG[data.kind].detailRoute(data.id)} replace />;
  }

  const title = isNew ? `New ${config.singular}` : (savedTitle ?? data?.title ?? "Edit dashboard");

  return (
    <div className={styles.wrapper}>
      <PageHeader title={title} subtitle={isNew ? undefined : config.singular}>
        <div className={styles.headerActions}>
          <Link className={styles.backLink} to={config.listRoute}>
            Back to {config.title}
          </Link>
        </div>
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading ? (
        <LoadingState message="Loading dashboard…" />
      ) : isNew || data ? (
        <DashboardForm
          key={data?.id ?? "new"}
          routeKind={kind}
          dashboard={data}
          canWrite={canWrite}
          onSaved={saved => setSavedTitle(saved.title)}
        />
      ) : null}
    </div>
  );
}

export default DashboardEdit;
